/**
 * Post-ATC Upsell
 * ================
 * Intercepts the theme's `cart:update` event (fired on every add-to-cart),
 * suppresses the cart drawer's auto-open for that tick, and shows a
 * one-click upsell modal with complementary products. On dismiss OR
 * after the user adds an upsell, we re-fire `cart:update` so the
 * drawer's auto-open behavior catches it normally.
 *
 * Safety gates:
 *  - Skipped in Shopify designMode.
 *  - Skipped on reduced-motion? No — this is UX, not animation. We still show it but without the entrance animation.
 *  - Suppressed once per session after dismissal.
 *  - Never re-enters itself (guarded by a module flag during handling).
 */

const DISMISS_KEY = 'he_post_atc_dismissed_session';
const SOURCE_TOKEN = 'post-atc-upsell-rebroadcast';

const isDesignMode = () => !!(typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode);

let isHandling = false;
let dialog = null;
let contentEl = null;
let closeBtn = null;
let dismissed = false;
// Preserved from the intercepted cart:update event so we can rebroadcast it
// intact. Without detail.data.sections, the cart-items component can't morph
// the drawer DOM (and the drawer opens on a stale empty cart).
let pendingCartDetail = null;

try {
  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(DISMISS_KEY) === '1') {
    dismissed = true;
  }
} catch (e) {}

function money(cents, currency) {
  const dollars = cents / 100;
  if (cents % 100 === 0) return '$' + dollars.toFixed(0);
  return '$' + dollars.toFixed(2);
}

function escapeHtml(str) {
  return String(str || '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
  );
}

function ensureDialog() {
  if (dialog) return dialog;
  dialog = document.querySelector('[data-he-post-atc-dialog]');
  if (!dialog) return null;
  contentEl = dialog.querySelector('[data-he-post-atc-content]');
  closeBtn = dialog.querySelector('[data-he-post-atc-close]');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => closeDialog('dismissed'));
  }
  // Close on backdrop click
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) closeDialog('dismissed');
  });
  // Close on Escape
  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    closeDialog('dismissed');
  });
  return dialog;
}

function renderUpsellCards(products, anchorTitle) {
  if (!contentEl) return;
  let html =
    '<p class="he-post-atc__added">Added to cart: <strong>' + escapeHtml(anchorTitle || 'Your item') + '</strong></p>' +
    '<h3 class="he-post-atc__heading">Complete your order</h3>' +
    '<p class="he-post-atc__sub">One-click add any of these — customers who bought yours often add these too.</p>' +
    '<div class="he-post-atc__grid">';
  for (const p of products) {
    const v = p.variants && p.variants[0];
    if (!v) continue;
    const img = (p.featured_image && (p.featured_image.src || p.featured_image)) || (p.images && p.images[0]) || '';
    html +=
      '<div class="he-post-atc__card" data-variant-id="' + v.id + '">' +
        (img ? '<img class="he-post-atc__img" src="' + escapeHtml(img) + '" loading="lazy" alt="' + escapeHtml(p.title) + '">' : '<div class="he-post-atc__img he-post-atc__img--placeholder"></div>') +
        '<div class="he-post-atc__card-body">' +
          '<span class="he-post-atc__title">' + escapeHtml(p.title) + '</span>' +
          '<span class="he-post-atc__price">' + money(v.price) + '</span>' +
        '</div>' +
        '<button type="button" class="he-post-atc__add" data-he-post-atc-add aria-label="Add ' + escapeHtml(p.title) + ' to cart">+ Add</button>' +
      '</div>';
  }
  html += '</div>';
  html += '<button type="button" class="he-post-atc__skip" data-he-post-atc-skip>No thanks, take me to my cart</button>';
  contentEl.innerHTML = html;

  // Wire adds
  contentEl.querySelectorAll('[data-he-post-atc-add]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const card = e.currentTarget.closest('[data-variant-id]');
      if (!card) return;
      const variantId = parseInt(card.dataset.variantId, 10);
      if (!Number.isFinite(variantId)) return;
      addUpsell(variantId, e.currentTarget);
    });
  });
  const skipBtn = contentEl.querySelector('[data-he-post-atc-skip]');
  if (skipBtn) skipBtn.addEventListener('click', () => closeDialog('dismissed'));
}

function addUpsell(variantId, btnEl) {
  if (btnEl) {
    btnEl.disabled = true;
    btnEl.textContent = 'Adding…';
  }
  const addCall = (window.HE && window.HE.cartAdd)
    ? window.HE.cartAdd([{ id: variantId, quantity: 1 }], { source: 'post-atc-upsell-inner' })
    : Promise.reject(new Error('HE.cartAdd helper not loaded'));

  addCall
    .then((data) => {
      if (btnEl) btnEl.textContent = 'Added ✓';
      if (window.HE && window.HE.trackEvent) {
        window.HE.trackEvent('he_post_atc_add', { variant_id: variantId });
      }
      // The helper already dispatched cart:update with full sections payload,
      // so the drawer DOM is up to date. Replace our preserved intercept detail
      // with this newer one so the rebroadcast on close uses the latest sections.
      pendingCartDetail = {
        resource: data,
        sourceId: 'post-atc-upsell-inner',
        data: { sections: (data && data.sections) || {}, itemCount: data && data.item_count },
      };
      setTimeout(() => closeDialog('accepted'), 700);
    })
    .catch((err) => {
      if (window.HE && window.HE.trackError) {
        window.HE.trackError('post-atc-upsell/addUpsell', err, { variant_id: variantId });
      }
      if (btnEl) {
        btnEl.disabled = false;
        btnEl.textContent = 'Try again';
      }
    });
}

function openDialog(anchorTitle, products) {
  const d = ensureDialog();
  if (!d) return;
  renderUpsellCards(products, anchorTitle);
  try {
    if (typeof d.showModal === 'function') d.showModal();
    else d.setAttribute('open', '');
  } catch (e) {
    d.setAttribute('open', '');
  }
  if (window.HE && window.HE.trackEvent) {
    window.HE.trackEvent('he_post_atc_shown', {
      anchor_title: anchorTitle,
      upsell_count: (products && products.length) || 0,
    });
  }
}

function closeDialog(reason) {
  // Close the dialog if it's actually open. Don't early-return if dialog isn't cached —
  // we still need to re-broadcast cart:update so the drawer's auto-open catches it
  // (especially for the no-upsells path where the dialog was never opened in the first place).
  if (dialog) {
    try {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    } catch (e) {
      dialog.removeAttribute('open');
    }
  }

  if (reason === 'dismissed') {
    dismissed = true;
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (e) {}
  }

  if (window.HE && window.HE.trackEvent) {
    window.HE.trackEvent('he_post_atc_closed', { reason: reason || 'closed' });
  }

  // Re-broadcast cart:update so the cart drawer's auto-open catches it now.
  // CRITICAL: spread the preserved original detail so cart-items component can
  // morph the drawer DOM (detail.data.sections). Without that, drawer opens empty.
  // Release the handling lock BEFORE dispatch so subsequent cart:update events after
  // this one don't get swallowed.
  isHandling = false;
  const detailToRebroadcast = {
    ...(pendingCartDetail || {}),
    source: SOURCE_TOKEN,
    reason: reason || 'closed',
  };
  pendingCartDetail = null;
  requestAnimationFrame(() => {
    document.dispatchEvent(new CustomEvent('cart:update', { detail: detailToRebroadcast }));
  });
}

async function handleCartAdd(event) {
  if (isHandling) return;
  if (isDesignMode()) return;
  if (dismissed) return;

  const detail = (event && event.detail) || {};
  // Skip our own rebroadcast and any other non-real triggers
  if (detail.source === SOURCE_TOKEN) return;

  // Only fire for a real user add. Require either event.detail.data.productId
  // or detail.source being a first-party product form.
  const productId = (detail.data && detail.data.productId) || detail.productId || null;
  if (!productId) return;

  // Preserve the original detail so we can rebroadcast it intact on close.
  // cart-items component reads detail.data.sections to morph the drawer DOM.
  pendingCartDetail = event.detail || null;

  // Suppress cart drawer auto-open for THIS event
  if (event.stopImmediatePropagation) event.stopImmediatePropagation();
  isHandling = true;

  // Fetch the anchor product metadata (for title + type) then ask the cross-sell
  // helper for picks that span DIFFERENT categories (map in cross-sell-map.json).
  let products = [];
  let anchorTitle = '';
  let productType = '';
  try {
    const prodHandle = detail.data && detail.data.productHandle ? detail.data.productHandle : '';
    const prod = prodHandle
      ? await fetch('/products/' + prodHandle + '.js', { credentials: 'same-origin' })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      : null;
    if (prod && prod.title) anchorTitle = prod.title;
    if (prod && prod.type) productType = prod.type;

    if (window.HE && typeof window.HE.getCrossSellPicks === 'function') {
      products = await window.HE.getCrossSellPicks({
        productId,
        productType,
        // /products/{handle}.js doesn't expose collections — by_collection lookup
        // is skipped for this path; by_type covers the taxonomy well enough.
        productCollections: [],
        limit: 3,
      });
    } else {
      // Helper not loaded — fall back to Shopify's complementary recs so the
      // modal still has something to show.
      const recs = await fetch(
        '/recommendations/products.json?product_id=' + encodeURIComponent(productId) + '&limit=3&intent=complementary',
        { credentials: 'same-origin' }
      ).then((r) => (r.ok ? r.json() : null)).catch(() => null);
      if (recs && recs.products) products = recs.products.slice(0, 3);
    }
  } catch (e) {}

  if (!products.length) {
    // No upsells to show — just let the drawer open normally by re-broadcasting
    closeDialog('no-upsells');
    return;
  }

  openDialog(anchorTitle, products);
}

if (typeof document !== 'undefined') {
  // Capture phase so we run before the cart drawer's default listener
  document.addEventListener('cart:update', handleCartAdd, { capture: true });
}

export {};
